import { useEffect, useRef, useState } from "react";
import { API_BASE, api } from "../lib/api";
import { MessageCircle, X, Send } from "lucide-react";
import { useSettings } from "../lib/settings";

import { tr } from "../lib/i18n";
const VISITOR_KEY = "csrm_visitor_id";
const CONV_KEY = "csrm_conversation_id";

export default function ChatWidget({ vehicleId = null, vehicleLabel = null }) {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [visitorId, setVisitorId] = useState(() => localStorage.getItem(VISITOR_KEY) || "");
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [started, setStarted] = useState(false);
  const listRef = useRef(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);

  const scrollBottom = () => setTimeout(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, 40);

  const start = async () => {
    if (!name.trim() || !phone.trim()) return;
    const { data } = await api.post("/chat/visitor/start", {
      visitor_id: visitorId || undefined,
      name, phone, email, vehicle_id: vehicleId, vehicle_label: vehicleLabel,
    });
    setVisitorId(data.visitor_id);
    localStorage.setItem(VISITOR_KEY, data.visitor_id);
    setConv(data.conversation);
    localStorage.setItem(CONV_KEY, data.conversation.id);
    setStarted(true);
    await loadMessages(data.visitor_id, data.conversation.id);
    openSocket(data.visitor_id, data.conversation.id);
  };

  const loadMessages = async (vid, cid) => {
    const { data } = await api.get("/chat/visitor/messages", { params: { visitor_id: vid, conversation_id: cid } });
    setMessages((prev) => {
      // Preserve any not-yet-echoed optimistic bubbles
      const optimistic = prev.filter(
        (m) => String(m.id).startsWith("opt-") &&
          !data.messages.some((s) => s.sender_type === m.sender_type && s.text === m.text),
      );
      return [...data.messages, ...optimistic];
    });
    scrollBottom();
  };

  const openSocket = (vid, cid) => {
    try {
      const url = API_BASE.replace(/^http/, "ws") + `/chat/ws/visitor?visitor_id=${encodeURIComponent(vid)}&conversation_id=${encodeURIComponent(cid)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      // Safety net: poll as well, so replies arrive even if the socket is silently dropped by a proxy.
      if (!pollRef.current) pollRef.current = setInterval(() => loadMessages(vid, cid).catch(() => {}), 4000);
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "message" && data.conversation_id === cid) {
            setMessages((prev) => {
              // Skip if id already present
              if (prev.find((m) => m.id === data.message.id)) return prev;
              // If it's the visitor's own message echoing back, replace the optimistic bubble
              if (data.message.sender_type === "visitor") {
                const idx = prev.findIndex(
                  (m) => String(m.id).startsWith("opt-") &&
                    m.sender_type === "visitor" &&
                    m.text === data.message.text,
                );
                if (idx >= 0) {
                  const next = [...prev];
                  next[idx] = data.message;
                  return next;
                }
              }
              return [...prev, data.message];
            });
            scrollBottom();
          }
        } catch {}
      };
      ws.onclose = () => {
        // Fall back to polling
        if (!pollRef.current) {
          pollRef.current = setInterval(() => loadMessages(vid, cid), 4000);
        }
      };
    } catch {}
  };

  // Auto-resume
  useEffect(() => {
    if (!open) return;
    const vid = localStorage.getItem(VISITOR_KEY);
    const cid = localStorage.getItem(CONV_KEY);
    if (vid && cid && !started) {
      (async () => {
        try {
          const { data } = await api.get("/chat/visitor/messages", { params: { visitor_id: vid, conversation_id: cid } });
          if (data && Array.isArray(data.messages)) {
            setVisitorId(vid);
            setConv({ id: cid, status: data.status });
            setMessages(data.messages);
            setStarted(true);
            scrollBottom();
            openSocket(vid, cid);
          }
        } catch {
          // Stale conversation — reset
          localStorage.removeItem(CONV_KEY);
        }
      })();
    }
    return () => {
      try { wsRef.current?.close(); } catch {}
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [open]); // eslint-disable-line

  const send = async () => {
    if (!text.trim() || !conv) return;
    const t = text.trim();
    setText("");
    // Optimistic
    const opt = { id: `opt-${Date.now()}`, sender_type: "visitor", sender_name: name, text: t, created_at: new Date().toISOString() };
    setMessages((p) => [...p, opt]);
    scrollBottom();
    await api.post("/chat/visitor/send", { visitor_id: visitorId, conversation_id: conv.id, text: t });
  };

  const businessOpen = (() => {
    const s = settings?.chat_business_hours_start;
    const e = settings?.chat_business_hours_end;
    if (!s || !e) return true;
    const now = new Date();
    const cur = now.getHours() * 60 + now.getMinutes();
    const parse = (v) => { const [h, m] = v.split(":").map(Number); return (h || 0) * 60 + (m || 0); };
    return cur >= parse(s) && cur <= parse(e);
  })();

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-widget-launcher"
          className="pub-chat-fab fixed bottom-24 end-5 z-[55] w-14 h-14 rounded-full bg-accent-brand text-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
          aria-label={tr("x.open_chat")}
        >
          <MessageCircle size={22} />
        </button>
      )}

      {open && (
        <div data-testid="chat-widget" className="fixed z-[60] shadow-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl overflow-hidden flex flex-col
          bottom-0 end-0 w-full h-full md:bottom-5 md:end-5 md:w-96 md:h-[560px] md:rounded-2xl">
          <div className="bg-accent-brand text-white p-4 flex items-center justify-between">
            <div>
              <div className="font-semibold" style={{ fontFamily: "Outfit" }}>{settings?.showroom_name || tr("x.chat")}</div>
              <div className="text-xs opacity-80">{businessOpen ? tr("x.we_re_online_reply_in_minutes") : (settings?.chat_offline_message || tr("x.we_re_currently_offline"))}</div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded"><X size={18} /></button>
          </div>

          {!started ? (
            <form onSubmit={(e) => { e.preventDefault(); start(); }} className="p-5 space-y-3 flex-1">
              <div className="text-sm text-[var(--gray-400)]">{tr("x.please_share_a_few_details_so_we_can_hel")}</div>
              <input data-testid="visitor-name" placeholder={tr("x.full_name")} value={name} onChange={(e) => setName(e.target.value)} required className="w-full h-11 rounded-lg border border-[hsl(var(--border))] px-3 text-sm" />
              <input data-testid="visitor-phone" placeholder={tr("x.mobile")} value={phone} onChange={(e) => setPhone(e.target.value)} required className="w-full h-11 rounded-lg border border-[hsl(var(--border))] px-3 text-sm" />
              <input data-testid="visitor-email" placeholder={tr("x.email_optional")} value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-11 rounded-lg border border-[hsl(var(--border))] px-3 text-sm" />
              {vehicleLabel && <div className="text-xs text-[var(--gray-400)]">{tr("x.enquiring_about")} <span className="font-semibold">{vehicleLabel}</span></div>}
              <button data-testid="visitor-start" type="submit" className="w-full h-11 rounded-lg bg-accent-brand text-white text-sm font-semibold">{tr("x.start_chat")}</button>
            </form>
          ) : (
            <>
              <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-[hsl(var(--muted))]/40">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_type === "visitor" ? "justify-end" : m.sender_type === "system" ? "justify-center" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                      m.sender_type === "visitor" ? "bg-accent-brand text-white" :
                      m.sender_type === "system" ? "bg-transparent text-[var(--gray-400)] text-[11px] italic" :
                      "bg-[hsl(var(--card))] border border-[hsl(var(--border))]"
                    }`}>
                      {m.sender_type !== "system" && m.sender_type !== "visitor" && <div className="text-[10px] opacity-60 mb-0.5">{m.sender_name || (m.sender_type === "bot" ? tr("x.assistant") : tr("x.agent"))}</div>}
                      <div className="whitespace-pre-wrap">{m.text}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-[hsl(var(--border))] p-3 flex gap-2">
                <input
                  data-testid="visitor-message-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
                  placeholder={tr("x.type_a_message")}
                  className="flex-1 h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm"
                />
                <button data-testid="visitor-send" onClick={send} disabled={!text.trim()} className="h-10 px-3 rounded-lg bg-accent-brand text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-40">
                  <Send size={14} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
